/**
 * @fileoverview Hook responsável por autenticar o usuário, renovar a sessão e manter o contexto do usuário sincronizado.
 */

import { useState, useEffect, useContext, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { has, isArray } from "lodash";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { SocketContext } from "../../context/Socket/SocketContext";
import moment from "moment";
// Decodifica o payload de um JWT sem validar assinatura — só pra ler claims
// que o frontend precisa (ex: bloco `impersonation`). Validação de fato fica
// no backend via isAuth middleware.
const decodeJwtPayload = (token) => {
  if (!token) return null;
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    // Base64URL → Base64 padding + decode
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
};

const useAuth = () => {
  const history = useHistory();
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});
  // Espelho do claim `impersonation` do JWT (ou null). Persistido no token,
  // então sobrevive a refresh sem precisar de storage separado.
  const [impersonation, setImpersonation] = useState(null);

  // Removido interceptors duplicados - agora estão no api.js

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    const token = localStorage.getItem("token");

    // Não tentar refresh se está em páginas públicas
    const publicRoutes = ['/login', '/signup'];
    const isPublicRoute = publicRoutes.some(route =>
      window.location.pathname.startsWith(route)
    );

    if (isPublicRoute) {
      // Limpar qualquer token residual silenciosamente
      localStorage.removeItem("token");
      localStorage.removeItem("companyId");
      localStorage.removeItem("userId");
      setIsAuth(false);
      setLoading(false);
      return;
    }

    (async () => {
      if (token) {
        try {
          const { data } = await api.post("/auth/refresh_token");
          api.defaults.headers.Authorization = `Bearer ${data.token}`;
          localStorage.setItem("token", data.token);
          setIsAuth(true);
          setUser(data.user);
          // Recupera impersonação do JWT (sobrevive a F5)
          const decoded = decodeJwtPayload(data.token);
          setImpersonation(decoded?.impersonation || null);
        } catch (err) {
          // Se refresh falhar, limpar token e continuar
          localStorage.removeItem("token");
          localStorage.removeItem("companyId");
          localStorage.removeItem("userId");
          setIsAuth(false);
          setImpersonation(null);
        }
      }
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    if (companyId) {
      const socket = socketManager.getSocket(companyId);

      socket.on(`company-${companyId}-user`, (data) => {
        if (data.action === "update" && data.user.id === user.id) {
          setUser(data.user);
        }
      });

      return () => {
        socket.disconnect();
      };
    }
  }, [socketManager, user]);

  const finalizeSession = (data) => {
    const {
      user: { companyId, id, company },
    } = data;

    if (has(company, "settings") && isArray(company.settings)) {
      const setting = company.settings.find(
        (s) => s.key === "campaignsEnabled"
      );
      if (setting && setting.value === "true") {
        localStorage.setItem("cshow", null);
      }
    }

    localStorage.setItem("token", data.token);
    localStorage.setItem("companyId", companyId);
    localStorage.setItem("userId", id);
    api.defaults.headers.Authorization = `Bearer ${data.token}`;
    setUser(data.user);
    setIsAuth(true);
    const decoded = decodeJwtPayload(data.token);
    setImpersonation(decoded?.impersonation || null);
    toast.success(i18n.t("auth.toasts.success"));
    history.push("/tickets");
  };

  // Aplica um novo token (de impersonate/switch/return) sem redirect.
  // Forçamos reload da página: muitos componentes (sockets, listas, contexto
  // de Tickets) inicializam com companyId fixo no mount — sem reload o estado
  // antigo persiste e mistura tudo.
  const applyTokenAndReload = (data, redirectTo = "/") => {
    localStorage.setItem("token", data.token);
    localStorage.setItem("companyId", data.user.companyId);
    localStorage.setItem("userId", data.user.id);
    api.defaults.headers.Authorization = `Bearer ${data.token}`;
    window.location.assign(redirectTo);
  };

  /**
   * Super-admin entra no contexto de outro usuário sem fazer logout.
   * Backend embeda claim `impersonation` no JWT; após reload, useAuth detecta
   * isso e o ImpersonationBanner aparece automaticamente.
   */
  const impersonateUser = async (userId) => {
    try {
      const { data } = await api.post(`/auth/impersonate/${userId}`);
      applyTokenAndReload(data, "/tickets");
      return { ok: true };
    } catch (err) {
      toastError(err);
      return { error: true };
    }
  };

  /**
   * Super-admin entra no contexto de outra Empresa (Conta) sem logout.
   * Não-destrutivo: o User.companyId no DB não muda, é só uma view de sessão.
   */
  const superSwitchCompany = async (companyId) => {
    try {
      const { data } = await api.post(`/auth/super-switch-company/${companyId}`);
      applyTokenAndReload(data, "/tickets");
      return { ok: true };
    } catch (err) {
      toastError(err);
      return { error: true };
    }
  };

  /**
   * Lista as Contas da Empresa (groupName) atualmente ativa pro super que
   * está impersonando. Usado pelo ImpersonationBanner pra popular o controle
   * de navegação entre Contas (setinhas e dropdown).
   *
   * Retorna `{ contas, currentIndex, groupName }` ou null em erro.
   *
   * useCallback é OBRIGATÓRIO aqui: o ImpersonationBanner depende dessa
   * função no array de deps do useEffect. Sem useCallback, cada re-render
   * do AuthProvider cria nova referência → useEffect re-roda → o fetch
   * anterior é cancelado (cancelled=true) antes de chegar no setState →
   * `contas` fica vazio pra sempre → setinhas de troca de Conta não
   * aparecem.
   */
  const listSiblingContas = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/impersonate/sibling-contas");
      return data;
    } catch (err) {
      // Silencioso — controle de troca de Conta simplesmente não aparece se
      // a chamada falhar (ex: super perdeu permissão entre tabs).
      return null;
    }
  }, []);

  /** Restaura sessão original do super-admin a partir do claim do JWT atual. */
  const returnFromImpersonation = async () => {
    try {
      const { data } = await api.post("/auth/impersonate/return");
      applyTokenAndReload(data, "/companies");
      return { ok: true };
    } catch (err) {
      toastError(err);
      return { error: true };
    }
  };

  const handleLogin = async (userData) => {
    setLoading(true);

    try {
      const { data } = await api.post("/auth/login", userData);

      if (data.mfaRequired) {
        setLoading(false);
        return { mfaRequired: true, mfaTicket: data.mfaTicket };
      }
      if (data.mfaSetupRequired) {
        setLoading(false);
        return { mfaSetupRequired: true, setupTicket: data.setupTicket };
      }

      finalizeSession(data);
      setLoading(false);
      return { ok: true, mfaGraceDaysLeft: data.mfaGraceDaysLeft };
    } catch (err) {
      toastError(err);
      setLoading(false);
      return { error: true };
    }
  };

  const handleLoginMfa = async ({ mfaTicket, otp, backupCode }) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login/mfa", {
        mfaTicket,
        otp,
        backupCode,
      });
      finalizeSession(data);
      setLoading(false);
      return { ok: true };
    } catch (err) {
      toastError(err);
      setLoading(false);
      return { error: true };
    }
  };

  const finalizeMfaSetupSession = (data) => {
    finalizeSession(data);
  };

  const handleLogout = async () => {
    setLoading(true);

    try {
      await api.delete("/auth/logout");
      setIsAuth(false);
      setUser({});
      localStorage.removeItem("token");
      localStorage.removeItem("companyId");
      localStorage.removeItem("userId");
      localStorage.removeItem("cshow");
      api.defaults.headers.Authorization = undefined;
      setLoading(false);
      history.push("/login");
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const getCurrentUserInfo = async () => {
    try {
      const { data } = await api.get("/auth/me");
      return data;
    } catch (err) {
      // Não mostrar erro para getCurrentUserInfo, apenas retornar null
      // Isso evita logout desnecessário
      return null;
    }
  };

  return {
    isAuth,
    user,
    loading,
    impersonation,
    handleLogin,
    handleLoginMfa,
    finalizeMfaSetupSession,
    handleLogout,
    getCurrentUserInfo,
    impersonateUser,
    superSwitchCompany,
    listSiblingContas,
    returnFromImpersonation,
  };
};

export default useAuth;
